import TextTooltip from "components/shared/TextTooltip";
import formatMoneyValue from "components/utils/formatMoneyValue";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { useState } from "react";
import orderBy from "lodash/fp/orderBy";
import classNames from "components/ui/classNames";

interface IEquityInterestsListProps {
  equityInterests: IBrazilianCompanyIssuerTopLevelFragment["equityInterests"];
}

export default function EquityInterestsList({ equityInterests }: IEquityInterestsListProps) {
  const [showDetailsForIndex, setShowDetailsForIndex] = useState<number | null>(null);

  return (
    <div className="overflow-hidden rounded bg-white p-2">
      <table className="min-w-full border-collapse border-inherit text-left indent-0 text-sm tracking-wide">
        <thead className="bg-white">
          <tr>
            <th className="w-[70%] px-4 py-3 font-semibold uppercase text-neutral-700">Denominação Social</th>
            <th className="px-4 py-3 text-center font-semibold uppercase text-neutral-700">Participação (%)</th>
          </tr>
        </thead>

        <tbody>
          {orderBy(["participation"], ["desc"], equityInterests).map((node, index) => {
            const isShowingDetails = showDetailsForIndex === index;

            return (
              <tr
                key={`${node.name}/${String(index)}`}
                className={classNames(
                  "group border-t border-neutral-200",
                  "hover:border-t-transparent [&:hover+tr]:border-t-transparent",
                )}
              >
                <td
                  className={classNames(
                    "px-4 py-3 group-hover:bg-neutral-50",
                    "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  <div className="grid gap-y-2">
                    <strong className="text-base text-slate-500">{node.name}</strong>

                    <div>
                      <span className="text-sm text-slate-400">
                        Valor de Contábil: <strong>{formatMoneyValue(node.bookValue)}</strong>
                      </span>
                    </div>

                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowDetailsForIndex(isShowingDetails ? null : index);
                        }}
                        className="m-0 cursor-pointer appearance-none border-none bg-none p-0 text-sm tracking-wider text-blue-700"
                      >
                        {isShowingDetails ? "Esconder detalhes" : "Ver descrição das atividades"}
                      </button>
                    </div>
                  </div>

                  {isShowingDetails && (
                    <p className="rounded-lg bg-neutral-100 p-3 text-base tracking-wider text-neutral-700">
                      {node.descriptionOfActivitiesDeveloped}
                    </p>
                  )}
                </td>
                <td
                  className={classNames(
                    "px-4 py-3 text-center group-hover:bg-neutral-50",
                    "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  <span>{Math.ceil(node.participation)}%</span>
                </td>
                <td
                  className={classNames(
                    "px-4 py-3 text-right group-hover:bg-neutral-50",
                    "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  {node.geographicalCountry && (
                    <div>
                      <TextTooltip
                        key={node.geographicalCountry.isoCode}
                        id={`${node.name}/${node.geographicalCountry.isoCode}`}
                        placement="top"
                        content={node.geographicalCountry.name}
                      >
                        {({ accessibility, bind, ref }) => (
                          <img
                            {...accessibility}
                            {...bind}
                            ref={ref}
                            title={node.geographicalCountry?.name}
                            alt={node.geographicalCountry?.name}
                            src={node.geographicalCountry?.flagSvg.ovalHref}
                            width={24}
                            height={24}
                          />
                        )}
                      </TextTooltip>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
